import React, { FC, useCallback, useEffect, useState } from 'react'
import { NarrationModel } from '@store/slices/narration.slice'
import { createEditor, Range, Transforms } from 'slate'
import { Editable, ReactEditor, Slate, withReact } from 'slate-react'
import { ButtonGroup, ToggleButton } from 'react-bootstrap'
import { PromethistStatus } from '@shared/enums'
import { PromethistDialogue } from '@shared/classes'
import { useTheme } from '@config/theme.config'
import AppButton from '@components/shared/appButton'
import { Trash } from 'react-bootstrap-icons'
import { selectAllVoices } from '@store/slices/voice.slice'
import { useSelector } from 'react-redux'
import { useNarrationFormContext } from '@components/narration/narration.component'

const DialogueEditorComponent: FC = () => {
  const [editor] = useState(() => withReact(createEditor()))
  const {
    setNarrationInEditing,
    narrationInEditing,
    dialogNumber,
    setDialogNumber,
  } = useNarrationFormContext()
  const selectedDialogue = narrationInEditing?.dialogue.sessions[dialogNumber]
  const { theme } = useTheme()
  const [rerenderFlag, setRerenderFlag] = useState(false)
  const voices = useSelector(selectAllVoices)
  const voice1 = voices.find((voice) => voice.isDialogueFirstPersonDefault)
  const voice2 = voices.find((voice) => voice.isDialogueSecondPersonDefault)

  useEffect(() => {
    if (voice1 || voice2) forceRerender()
  }, [voice1, voice2])

  const forceRerender = () => {
    setRerenderFlag((prev) => !prev)
  }

  const updateEditingNarration = (dialogue: PromethistDialogue) => {
    if (narrationInEditing) {
      const updatedNarration = { ...narrationInEditing }
      const updateTranscription = {
        ...narrationInEditing.dialogue,
        sessions: [...narrationInEditing.dialogue.sessions],
      }
      updateTranscription.sessions[dialogNumber] = dialogue

      updatedNarration['dialogue'] = updateTranscription
      setNarrationInEditing(updatedNarration as NarrationModel)
    }
  }

  const initialValue = () => {
    return selectedDialogue && selectedDialogue.transcript.length
      ? selectedDialogue.transcript.map((sentence) => ({
          type: 'sentence',
          personaName: sentence.personaName,
          children: [{ text: sentence.transcript }],
        }))
      : []
  }

  const renderElement = useCallback(
    (props: any) => {
      const elementPath = ReactEditor.findPath(editor, props.element)
      const elementIndex = elementPath[elementPath.length - 1]

      switch (props.element.type) {
        case 'sentence':
          return <SentenceElement {...props} elementIndex={elementIndex} />
        default:
          return <p {...props.attributes}>{props.children}</p>
      }
    },
    [editor, rerenderFlag]
  )

  const renderLeaf = useCallback((props: any) => {
    return <span {...props.attributes}>{props.children}</span>
  }, [])

  const SentenceElement = (props: any) => {
    const { element, attributes, children, elementIndex } = props
    const { personaName } = element

    const handleRemoveSentence = () => {
      if (selectedDialogue) {
        // Filter the transcript to remove the sentence at elementIndex
        const updatedTranscript = selectedDialogue.transcript.filter(
          (_: any, i: number) => i !== elementIndex
        )
        const updatedDialogue = {
          ...selectedDialogue,
          transcript: updatedTranscript,
        }

        updateEditingNarration(updatedDialogue)

        // Remove node from Slate editor
        Transforms.removeNodes(editor, { at: [elementIndex] })

        // Force a re-render to recalculate indices
        ReactEditor.focus(editor)
        Transforms.select(editor, { path: [0, 0], offset: 0 })
        forceRerender()
      }
    }

    const isElementFocused = () => {
      const { selection } = editor
      if (selection) {
        return Range.includes(selection, [elementIndex])
      }
      return false
    }

    return (
      <div
        className={`d-flex ${personaName == voice1?.name ? 'justify-content-start' : 'justify-content-end'}`}
      >
        <div
          {...attributes}
          className={`d-flex flex-column p-2 ${isElementFocused() ? 'bg-primary text-bg-primary' : theme == 'dark' ? 'bg-secondary' : 'bg-secondary-subtle'}`}
          style={{
            width: 'fit-content',
            maxWidth: '80%',
            borderRadius: '0.25rem',
            backgroundColor: '#212529',
          }}
        >
          <div
            className={`d-flex gap-2 align-items-center ${personaName == voice1?.name ? '' : 'flex-row-reverse'}`}
            style={{ pointerEvents: 'none', userSelect: 'none' }}
          >
            <AppButton
              buttonVariant={'danger'}
              buttonSize={'sm'}
              onClick={handleRemoveSentence}
              buttonChild={<Trash />}
              style={{ pointerEvents: 'auto' }}
            ></AppButton>
            <span className={`mb-0`}>
              <strong>{personaName}</strong>
            </span>
          </div>
          {children}
        </div>
      </div>
    )
  }

  const addBlankSentence = (personaName: string) => {
    if (!selectedDialogue) return
    const updatedDialogue = {
      ...selectedDialogue,
      transcript: [
        ...selectedDialogue.transcript,
        { personaName, transcript: '' },
      ],
    }
    const newNode = {
      type: 'sentence',
      personaName: personaName,
      children: [{ text: '' }],
    }
    Transforms.insertNodes(editor, newNode)
    forceRerender()
    updateEditingNarration(updatedDialogue)
  }

  const handleEditorChange = (value: any) => {
    if (selectedDialogue) {
      const updatedTranscript = value.map((node: any) => ({
        personaName: node.personaName,
        transcript: node.children[0].text,
      }))
      const updatedDialogue = {
        ...selectedDialogue,
        transcript: updatedTranscript,
      }
      updateEditingNarration(updatedDialogue)
    }
  }

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault()

      const { selection } = editor
      if (selection && Range.isCollapsed(selection)) {
        const text = `\n`

        // Insert the newline character at the current selection
        Transforms.insertText(editor, text)
      }
    }
  }

  return (
    <div className={'d-flex flex-column'}>
      <div className={'d-flex justify-content-between mb-2'}>
        {narrationInEditing &&
          narrationInEditing.dialogue.sessions.length > 1 &&
          voice1 &&
          voice2 && (
            <div className={'d-flex gap-1'}>
              <AppButton
                onClick={() => addBlankSentence(voice1?.name)}
                buttonText={`+${voice1 ? voice1.name : 'Postava 1'}`}
              />
              <AppButton
                onClick={() => addBlankSentence(voice2?.name)}
                buttonText={`+${voice2 ? voice2.name : 'Postava 2'}`}
              />
            </div>
          )}
        <div className={'d-flex align-items-center mb-2 gap-2'}>
          Vygenerované dialogy (
          {
            narrationInEditing?.dialogue.sessions.filter(
              (session) => session.status === PromethistStatus.FINISHED
            ).length
          }
          ):{''}
          <ButtonGroup>
            {narrationInEditing &&
              narrationInEditing.dialogue.sessions.map((dialogue, index) => {
                return dialogue.status === PromethistStatus.FINISHED ? (
                  <ToggleButton
                    id={'text'}
                    type="radio"
                    variant="secondary"
                    size={'sm'}
                    checked={dialogNumber === index}
                    onChange={() => setDialogNumber(index)}
                    disabled={dialogNumber === index}
                    value={index}
                    key={`dialog-${index}`}
                  >
                    {index + 1}
                  </ToggleButton>
                ) : (
                  ''
                )
              })}
          </ButtonGroup>
        </div>
      </div>
      {selectedDialogue && selectedDialogue.transcript.length > 0 ? (
        <div
          className="overflow-scroll d-felx flex-column"
          style={{
            maxHeight: '60vh',
            scrollbarWidth: 'auto',
            width: '100%',
            border:
              theme === 'dark' ? '1px solid #ced4da' : '1px solid #495057',
            borderRadius: '0.25rem',
          }}
        >
          <Slate
            editor={editor}
            initialValue={initialValue()}
            onChange={handleEditorChange}
          >
            <Editable
              renderElement={renderElement}
              renderLeaf={renderLeaf}
              onKeyDown={handleKeyDown}
              className={'d-flex flex-column gap-1 p-2'}
            />
          </Slate>
        </div>
      ) : (
        <span className={'text-secondary mt-2'}>
          Vybraný dialog neobsahuje text. Zkuste dialog přegenerovat.
        </span>
      )}
    </div>
  )
}

export default DialogueEditorComponent
